// ============================================================
// FILE: theme.js
// LOCATION: C:\gardenmarket\web\src\theme\theme.js
// ============================================================
// PURPOSE:
//   Shared design tokens. Color values updated for better
//   contrast and readability:
//     - Body text darker (#111827 vs old #1f2937)
//     - Secondary text stronger (#374151 vs old #6b7280)
//     - Faint text visible (#6B7280 vs old #9ca3af)
//     - Card borders visible (#C8DDD1 vs old #f1f5f9)
//     - Brand light more vivid (#DCFCE7 vs old #e8f5e9)
//     - Page background slightly warmer (#F5F9F6)
// ============================================================

export const colors = {
    brand:        '#2d6a4f',
    brandDark:    '#1B4332',
    brandLight:   '#DCFCE7',
    text:         '#111827',
    textMuted:    '#374151',
    textFaint:    '#6B7280',
    border:       '#C8DDD1',
    borderSoft:   '#D1E8DA',
    bgPage:       '#F5F9F6',
    bgCard:       '#ffffff',
    selectedTint: '#E8F5EE',
    selectedBar:  '#2d6a4f',
};

// ── Pastel status colours — slightly more vivid than before ──
export const statusColours = {
    draft:       { bg: '#F1F2F4', text: '#4B5563' },
    open:        { bg: '#DBEAFE', text: '#1E40AF' },
    matched:     { bg: '#DBEAFE', text: '#1E40AF' },
    quoted:      { bg: '#FEF3C7', text: '#92400E' },
    accepted:    { bg: '#DCFCE7', text: '#14532D' },
    in_progress: { bg: '#CFFAFE', text: '#164E63' },
    completed:   { bg: '#D1FAE5', text: '#065F46' },
    reviewed:    { bg: '#E2E8F0', text: '#1E3A5F' },
    closed:      { bg: '#F1F2F4', text: '#4B5563' },
};

export const buyerStatusLabels = {
    draft:       'Draft',
    open:        'Open — waiting for quotes',
    matched:     'Open — gardeners notified',
    quoted:      'Quotes received',
    accepted:    'Gardener booked',
    in_progress: 'In progress',
    completed:   'Completed',
    reviewed:    'Reviewed',
    closed:      'Withdrawn',
};

export const gardenerStatusLabels = {
    open:        'Open',
    matched:     'Matched to you',
    quoted:      'You quoted',
    accepted:    'Booked',
    in_progress: 'In progress',
    completed:   'Completed',
    reviewed:    'Reviewed',
};

export function statusPill(statusCode, viewer = 'buyer') {
    const c = statusColours[statusCode] || statusColours.draft;
    const labels = viewer === 'gardener' ? gardenerStatusLabels : buyerStatusLabels;
    const label  = labels[statusCode] || statusCode;
    return {
        backgroundColor: c.bg,
        color:           c.text,
        fontWeight:      '600',
        fontSize:        '0.74rem',
        padding:         '4px 11px',
        borderRadius:    radius.pill,
        display:         'inline-block',
        whiteSpace:      'nowrap',
        label,
    };
}

export const radius = {
    sm:   '8px',
    md:   '12px',
    lg:   '16px',
    pill: '999px',
};

export const shadow = {
    soft: '0 4px 6px -1px rgb(0 0 0 / 0.06), 0 2px 4px -2px rgb(0 0 0 / 0.06)',
    card: '0 1px 3px rgb(0 0 0 / 0.06)',
};

export const space = {
    xs: '6px', sm: '10px', md: '16px', lg: '24px', xl: '32px',
};

export const card = {
    background:   colors.bgCard,
    borderRadius: radius.lg,
    boxShadow:    shadow.soft,
    border:       `1px solid ${colors.border}`,
    padding:      space.lg,
    marginBottom: space.md,
};

export const cardHeader = {
    fontSize:      '0.72rem',
    fontWeight:    700,
    color:         colors.textFaint,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom:  space.sm,
};

export function listRow(isSelected) {
    return {
        display:             'grid',
        gridTemplateColumns: '56px 1fr 80px',
        gap:                 '10px',
        padding:             '14px 16px',
        borderBottom:        `1px solid ${colors.border}`,
        borderLeft:          isSelected ? `3px solid ${colors.selectedBar}` : '3px solid transparent',
        backgroundColor:     isSelected ? colors.selectedTint : 'transparent',
        cursor:              'pointer',
        alignItems:          'start',
    };
}

export const rowTitle = {
    fontSize:     '0.93rem',
    fontWeight:   700,
    color:        colors.text,
    whiteSpace:   'nowrap',
    overflow:     'hidden',
    textOverflow: 'ellipsis',
};

export const rowSubtitle = {
    fontSize:     '0.8rem',
    color:        colors.textFaint,
    whiteSpace:   'nowrap',
    overflow:     'hidden',
    textOverflow: 'ellipsis',
    marginTop:    '2px',
};

export const rowDate = {
    fontSize:   '0.78rem',
    color:      colors.textFaint,
    paddingTop: '2px',
};
